import { createI18n, type I18n, type LocaleMessages, type VueMessageType } from "vue-i18n";
import { baseLocales } from "@pixelpoint-pattern-library-v2/base/i18n/base-locales";
import { uiLocales } from "@ai-platform/ui/i18n/ui-locales.ts";

/*
 * vue-i18n for the portal (TL, 2026-10-12, AP-F6.5): the texts the pattern library's components
 * (`PixSelect`) and `packages/ui` (`InquiryForm`) bring, German only for now. The portal's own
 * texts stay where they are until the portal becomes multilingual; this is the place they would
 * move to, as a `src/i18n/locales/` merged last.
 */

export const PORTAL_LOCALE: string = "de";

type LocaleLoaders = Record<string, () => Promise<{ default: Record<string, unknown> }>>;

/** The messages of one locale from a package's loaders, nothing when it has no such file. */
async function messagesOf(loaders: LocaleLoaders, locale: string): Promise<Record<string, unknown>> {
  const load = Object.entries(loaders).find(([path]) => path.endsWith(`/${locale}.yaml`))?.[1];
  return load ? (await load()).default : {};
}

/** The packages' texts of one locale, later packages winning per key. */
async function localeMessages(locale: string): Promise<LocaleMessages<VueMessageType>> {
  const [library, ui] = await Promise.all([messagesOf(baseLocales, locale), messagesOf(uiLocales, locale)]);
  return { ...library, ...ui } as LocaleMessages<VueMessageType>;
}

/**
 * The portal's vue-i18n in `locale`; German is loaded as well, so a text missing in another
 * language falls back to German instead of showing its key.
 */
export async function createPortalI18n(locale: string = PORTAL_LOCALE): Promise<I18n> {
  const locales = locale === PORTAL_LOCALE ? [locale] : [locale, PORTAL_LOCALE];
  const messages = Object.fromEntries(
    await Promise.all(locales.map(async (code) => [code, await localeMessages(code)] as const)),
  );
  return createI18n({ legacy: false, locale, fallbackLocale: PORTAL_LOCALE, messages });
}
